import { useMemo } from 'react';
import type { ProjectsResponse } from '../../../../api/projects-client/projects-client.type';
import type { Epic } from '../../../../api/tasks-client/task-client.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useCostTabStore, useStrategyStore } from '../../../../store/strategy-store/strategy-store';
import { generateEpicsTreeMapData, generateTeamsTreeMapData } from './cost-tab.helpers';
import { CostMode, type ChartDataItem } from './cost-tab.type';

/**
 * Hook to retrieve epics that have associated costs in the current initiative
 *
 * @returns {Epic[] | []} Array of epics with cost data or empty array if no initiative or no matching epics
 */
const useCostEpics = (): Epic[] | [] => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);

  if (!initiative?.costs_to_date?.epics) {
    return [];
  }

  const { epics = [], costs_to_date } = initiative;

  const epicIds = Object.keys(costs_to_date.epics);
  const result = epics.filter((epic) => epicIds.includes(epic.id));

  return result;
};

/**
 * Hook to retrieve teams that have associated costs in the current initiative
 *
 * @returns {ProjectsResponse[] | []} Array of teams with cost data or empty array if no initiative or no matching teams
 */
const useCostTeams = (): ProjectsResponse[] | [] => {
  const globalTeams = useGlobalStore((state) => state.teams);
  const initiative = useStrategyStore((state) => state.initiativePerformance);

  if (!initiative?.costs_to_date?.teams) {
    return [];
  }

  const { costs_to_date } = initiative;

  const teamIds = Object.keys(costs_to_date.teams);
  const result = globalTeams.filter((team) => teamIds.includes(team.id));

  return result;
};

/**
 * Hook to retrieve chart data for the Cost tab visualization
 *
 * @returns {ChartDataItem[] | []} Array of chart data items or empty array if no initiative or no matching data
 */
const useChartData = (): ChartDataItem[] | [] => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const mode = useCostTabStore((state) => state.mode);
  const epics = useCostTabStore((state) => state.selectedEpics);
  const teams = useCostTabStore((state) => state.selectedTeams);

  return useMemo(() => {
    if (!initiative?.costs_to_date) {
      return [];
    }

    const { costs_to_date } = initiative;

    switch (mode) {
      case CostMode.ContributingEpics:
        return generateEpicsTreeMapData(costs_to_date, epics);
      case CostMode.ContributingTeams:
        return generateTeamsTreeMapData(costs_to_date, teams);
      default:
        return [];
    }
  }, [initiative, mode, epics, teams]);
};

export { useChartData, useCostEpics, useCostTeams };
